import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { createLegacyAccessKeys } from "../../navigation-v2/app/legacyAccessAdapter";
import { buildNavigationLocale, buildNavigationMessages } from "../../navigation-v2/app/messages";
import { readStoredUser, userIdFrom } from "../../navigation-v2/app/browserAdapters";
import { searchLegacyGlobalNavigation } from "../../navigation-v2/app/legacyGlobalSearch";

const SEARCH_DEBOUNCE_MS = 200;
const NOOP = () => {};
const CATEGORY_ICONS = Object.freeze({
  animals: "fas fa-paw",
  campaigns: "fas fa-clipboard-list",
  enclosures: "fas fa-home",
  enrichments: "fas fa-leaf",
  groups: "fas fa-users",
  navigation: "fas fa-arrow-right",
  resources: "fas fa-book",
  sectors: "fas fa-map-marked-alt",
  taxons: "fas fa-paw",
  tours: "fas fa-route",
  users: "fas fa-user",
});

const readViewer = () => (
  typeof window === "undefined" ? {} : readStoredUser(window.localStorage)
);

export default function LegacyGlobalNavigationSearch({
  history,
  i18n,
  location,
  onNavigateComplete = NOOP,
  searchProvider = searchLegacyGlobalNavigation,
  t,
  zoo,
}) {
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const generatedId = useId().replace(/:/g, "");
  const inputId = `legacy-global-search-input-${generatedId}`;
  const resultsId = `legacy-global-search-results-${generatedId}`;
  const requestSequence = useRef(0);
  const abortController = useRef(null);
  const searchTimer = useRef(null);
  const viewer = useMemo(() => {
    const storedViewer = readViewer();
    return storedViewer.zooId || !zoo.zooId
      ? storedViewer
      : { ...storedViewer, zooId: zoo.zooId };
  }, [zoo.zooId]);
  const accessKeys = useMemo(
    () => createLegacyAccessKeys({
      viewer,
      userRole: zoo.userRole,
      zooOptions: zoo.zooOptions,
      zooTier: zoo.zooTier,
    }),
    [viewer, zoo.userRole, zoo.zooOptions, zoo.zooTier]
  );
  const locale = useMemo(
    () => buildNavigationLocale(t, i18n?.language),
    [i18n?.language, t]
  );
  const messages = useMemo(() => buildNavigationMessages(t).search, [t]);

  useEffect(() => () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    abortController.current?.abort();
    requestSequence.current += 1;
  }, []);

  useEffect(() => setActiveIndex(0), [query, results]);

  const search = useCallback(async (query) => {
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    abortController.current?.abort();
    const controller = typeof AbortController === "function"
      ? new AbortController()
      : null;
    abortController.current = controller;

    try {
      const nextResults = await searchProvider({
        accessKeys,
        locale,
        location,
        query,
        signal: controller?.signal,
        userId: userIdFrom(viewer),
        zooId: zoo.zooId || viewer.zooId || viewer.userZoo?.value || null,
      });
      if (requestSequence.current === sequence) setResults(nextResults);
    } finally {
      if (requestSequence.current === sequence) setIsSearching(false);
    }
  }, [accessKeys, locale, location, searchProvider, viewer, zoo.zooId]);

  const navigate = useCallback((_destinationId, result) => {
    if (!result?.href) return;
    history.push(result.href);
    onNavigateComplete();
  }, [history, onNavigateComplete]);

  const requestSearch = useCallback((nextQuery, immediate = false) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setIsSearching(Boolean(nextQuery.trim()));
    const execute = () => search(nextQuery).catch(NOOP);
    if (immediate) execute();
    else searchTimer.current = setTimeout(execute, SEARCH_DEBOUNCE_MS);
  }, [search]);

  const choose = (result) => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    navigate(result.destinationId, result);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (!results.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === "Enter" && isOpen) {
      event.preventDefault();
      choose(results[activeIndex]);
    }
  };

  return (
    <div
      className="legacy-global-search"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
      }}
    >
      <label className="sr-only" htmlFor={inputId}>
        {messages.label}
      </label>
      <em className="icon-magnifier legacy-global-search__input-icon" aria-hidden="true" />
      <input
        id={inputId}
        type="search"
        role="combobox"
        className="legacy-global-search__input"
        value={query}
        placeholder={messages.placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={resultsId}
        onChange={(event) => {
          const nextQuery = event.target.value;
          setQuery(nextQuery);
          setIsOpen(true);
          requestSearch(nextQuery, nextQuery === "");
        }}
        onFocus={() => {
          setIsOpen(true);
          if (!results.length) requestSearch(query, true);
        }}
        onKeyDown={handleKeyDown}
      />

      <span className="sr-only" aria-live="polite">
        {isOpen ? messages.resultsCount({ count: results.length }) : ""}
      </span>

      {isOpen ? (
        <div className="legacy-global-search__results" id={resultsId} role="listbox" aria-label={messages.resultsLabel}>
          <div className="legacy-global-search__results-heading">
            {messages.resultsLabel}
          </div>
          {results.length ? results.map((result, index) => (
            <a
              key={result.id || result.destinationId}
              href={result.href}
              role="option"
              aria-selected={index === activeIndex}
              className={`legacy-global-search__result${index === activeIndex ? " is-active" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                choose(result);
              }}
            >
              <span className="legacy-global-search__result-icon" aria-hidden="true">
                <em className={CATEGORY_ICONS[result.category] || CATEGORY_ICONS.navigation} />
              </span>
              <span className="legacy-global-search__result-copy">
                <strong>{result.label}</strong>
                {result.parentLabel || result.description ? (
                  <small>{[result.parentLabel, result.description].filter(Boolean).join(" · ")}</small>
                ) : null}
              </span>
              <span className="legacy-global-search__result-category">
                {messages.groups[result.category || "navigation"] || result.category}
              </span>
            </a>
          )) : (
            <div className="legacy-global-search__empty">
              {isSearching ? messages.searchingLabel : messages.noResultsLabel}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

LegacyGlobalNavigationSearch.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  i18n: PropTypes.shape({ language: PropTypes.string }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    state: PropTypes.object,
  }).isRequired,
  onNavigateComplete: PropTypes.func,
  searchProvider: PropTypes.func,
  t: PropTypes.func.isRequired,
  zoo: PropTypes.shape({
    userRole: PropTypes.string,
    zooId: PropTypes.string,
    zooOptions: PropTypes.object,
    zooTier: PropTypes.string,
  }).isRequired,
};
